/**
 * yHelp Auth provider — wraps the SPA in Auth0Provider with the validated config.
 *
 * Keep this file thin: validation/shaping lives in `auth0-config.ts` so it can
 * be unit-tested without rendering React.
 *
 * Graceful degradation:
 *   In Sprint 0 a developer may run the SPA without filling in .env.local.
 *   To keep the landing page useful (showcase atoms, marketing copy) we
 *   render a "config missing" banner via <AuthProviderFallback> instead of
 *   blowing up the whole tree. In CI/CD the build job injects the real
 *   variables, so production is never affected by this fallback.
 */
import { Auth0Provider } from '@auth0/auth0-react';
import { type ReactNode } from 'react';
import { loadAuth0Config, type Auth0Config, type Auth0ConfigSource } from './auth0-config';

export interface AuthProviderProps {
  children: ReactNode;
  /** Override for tests; defaults to import.meta.env. */
  envSource?: Auth0ConfigSource;
  /** Override for tests; defaults to loadAuth0Config. */
  loadConfig?: (source: Auth0ConfigSource) => Auth0Config;
  /** Override for tests; defaults to console.warn. */
  onConfigError?: (err: unknown) => void;
}

export function AuthProvider({
  children,
  envSource,
  loadConfig = loadAuth0Config,
  onConfigError,
}: AuthProviderProps) {
  const env = envSource ?? (import.meta.env as unknown as Auth0ConfigSource);

  let config: Auth0Config | null = null;
  try {
    config = loadConfig(env);
  } catch (err) {
    (onConfigError ?? console.warn)(err);
    return (
      <>
        <AuthProviderFallback />
        {children}
      </>
    );
  }

  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      authorizationParams={config.authorizationParams}
      cacheLocation={config.cacheLocation}
      useRefreshTokens={config.useRefreshTokens}
      useRefreshTokensFallback
    >
      {children}
    </Auth0Provider>
  );
}

/**
 * Visible-but-unobtrusive banner shown when Auth0 env vars are missing.
 * Keeps the SPA renderable so dev iteration on atoms/marketing isn't blocked.
 */
export function AuthProviderFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900"
      data-testid="auth-fallback-banner"
    >
      ⚠️ Auth0 não configurado — copie <code>apps/web/.env.example</code> para{' '}
      <code>.env.local</code> e preencha as variáveis <code>VITE_AUTH0_*</code>.
    </div>
  );
}
